import React, { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import ModernForm from "./components/ModernForm.jsx";
import NoteManager from './components/NoteManager.jsx';
import UnifiedLogin from './components/UnifiedLogin.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Header from './components/Header.jsx';
import { toast } from 'react-hot-toast';
import { APP_VERSION } from './utils/version';
import UserProfile from './components/UserProfile';
import ShareReport from './components/ShareReport';
import DeletedReports from './components/DeletedReports';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './utils/firebase';
import NotificationBar from './components/NotificationBar.jsx';
import MessageView from './components/MessageView.jsx';

// Wrap the app content in a component that can access location
const AppContent = () => {
  const location = useLocation();
  const [user, loading] = useAuthState(auth);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(APP_VERSION);

  // Version check function
  const checkVersion = async () => {
    try {
      const response = await fetch('/version.json?' + new Date().getTime());
      const data = await response.json();
      
      setCurrentVersion(data.version);
      if (data.version !== APP_VERSION) {
        setShowUpdateButton(true);
      } else {
        setShowUpdateButton(false);
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  };

  const handleUpdate = () => {
    // Force reload from server, not from cache
    window.location.reload(true);
  };

  useEffect(() => {
    // Ensure document title is correct
    document.title = "OilTank.App";
    
    // Check version on mount and window focus
    checkVersion();
    window.addEventListener('focus', checkVersion);

    // Register service worker
    if ('serviceWorker' in navigator) {
      try {
        navigator.serviceWorker.register('/sw.js').then(registration => {
          console.log('Service Worker registered with scope:', registration.scope);
          
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            console.log('New service worker waiting');
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('Update available:', registration);
                checkVersion(); // Check version when service worker updates
              }
            });
          });
        });
      } catch (error) {
        // We're in private mode, skip service worker registration
        console.log('Private browsing mode detected, skipping service worker');
      }
    }

    // Cleanup
    return () => {
      window.removeEventListener('focus', checkVersion);
    };
  }, []);

  return (
    <>
      <Header />
      <NotificationBar />
      {showUpdateButton && currentVersion !== APP_VERSION && (
        <div className="fixed top-16 left-0 right-0 flex justify-center z-[9999] px-4 py-2">
          <button
            onClick={handleUpdate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
          >
            <span>🔄</span>
            New version available! Click to update
          </button>
        </div>
      )}
      <Routes>
        <Route path="/login" element={<UnifiedLogin />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ModernForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notes"
          element={
            <ProtectedRoute>
              <NoteManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deleted-reports"
          element={
            <ProtectedRoute>
              <DeletedReports />
            </ProtectedRoute>
          }
        />
        <Route path="/share-report/:reportId?" element={<ShareReport />} />
        <Route
          path="/message-view/:reportId"
          element={
            <ProtectedRoute>
              <MessageView />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;